// A text box with a dropdown, owned by us rather than by the browser.
//
// A native <input list> + <datalist> looks like the same thing and is not, in two ways that
// both bit real users:
//   - The browser filters the list down to an exact match, so once the box holds a value the
//     dropdown cannot be reopened without clearing it first.
//   - Combined with any code that normalises the text as you type, a value becomes
//     impossible to edit from the end: deleting the trailing " from `81"` leaves `81`, which
//     still matches, which puts the " straight back.
// This owns the list instead: the caret always opens the full set, and the text is only ever
// what the parent passes.
//
// PRESENTATIONAL ONLY. It knows nothing about answers, mappings or CPQ - callers decide what
// a value means. Two use it and they want different things: the Flange to Nozzle End box
// must resolve onto a real option, while the Housing Height cell is free text with
// suggestions, where a typed custom value is legitimate.
//
// The list is PORTALLED to document.body and positioned fixed. Inside a table it would
// otherwise be clipped: .cpq-table-wrap sets overflow-x: auto, and a computed overflow of
// auto on one axis is auto on both, so a dropdown from a lower row would be cut off.

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ghostView } from "../util/prediction";

export type ComboBoxOption = { value: string; label: string };

type Props = {
  value: string;
  options: ComboBoxOption[];
  /** Every keystroke, and the chosen value when an option is picked. */
  onChange: (value: string) => void;
  /** Blur, Enter, or an option being picked. Where a caller normalises or validates. */
  onCommit?: (value: string) => void;
  disabled?: boolean;
  /** Extra classes for the input (callers style their own invalid state). */
  inputClassName?: string;
  inputStyle?: React.CSSProperties;
  ariaInvalid?: boolean;
  /** Narrow the list as the user types. Off where the caller wants the full set always. */
  filterAsYouType?: boolean;
  /** The value is a PREDICTION (shown grey). Clicking in shows the box empty with the
   *  prediction as its placeholder (and the whole list); typing or picking replaces it; leaving
   *  without either brings it back (util/prediction ghostView). */
  replaceOnType?: boolean;
};

export default function ComboBox({
  value,
  options,
  onChange,
  onCommit,
  disabled = false,
  inputClassName = "",
  inputStyle,
  ariaInvalid,
  filterAsYouType = true,
  replaceOnType = false,
}: Props) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [rect, setRect] = useState<{ left: number; top: number; width: number } | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const listRef = useRef<HTMLUListElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  // Focused on a prediction nobody has typed over or picked yet: the box shows empty.
  const [focusedEmpty, setFocusedEmpty] = useState(false);
  const ghosting = replaceOnType && focusedEmpty;
  const shown = ghostView(ghosting, value);

  const listed = useMemo(() => {
    if (!filterAsYouType) return options;
    const typed = String(shown.value ?? "").trim().replace(/"/g, "").toLowerCase();
    if (!typed) return options;
    const narrowed = options.filter((o) => o.label.replace(/"/g, "").toLowerCase().startsWith(typed));
    // Narrowing to nothing shows everything rather than an empty box, so the control never
    // looks broken when someone types a value that is not in the list.
    return narrowed.length ? narrowed : options;
  }, [options, shown.value, filterAsYouType]);

  const place = useCallback(() => {
    const el = inputRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setRect({ left: r.left, top: r.bottom + 2, width: r.width });
  }, []);

  useLayoutEffect(() => {
    if (open) place();
  }, [open, place]);

  // A fixed-position list does not move with the page, so it has to be told. Capture phase,
  // because the scroll that matters is usually an ancestor's, not the window's.
  useEffect(() => {
    if (!open) return;
    const onScroll = () => place();
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
    };
  }, [open, place]);

  // Click anywhere else closes it. The list is portalled, so it is NOT inside the wrapper and
  // has to be checked separately - without that, clicking an option would close the list
  // before the click landed on it.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (wrapRef.current?.contains(t)) return;
      if (listRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const choose = (o: ComboBoxOption) => {
    setFocusedEmpty(false);
    setOpen(false);
    setActive(-1);
    onChange(o.value);
    onCommit?.(o.value);
    inputRef.current?.focus();
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      if (!open) {
        setOpen(true);
        setActive(0);
        return;
      }
      setActive((i) => {
        const n = listed.length;
        if (n === 0) return -1;
        return e.key === "ArrowDown" ? (i + 1) % n : (i - 1 + n) % n;
      });
      return;
    }
    if (e.key === "Enter") {
      if (open && active >= 0 && active < listed.length) {
        e.preventDefault();
        choose(listed[active]);
      } else {
        setOpen(false);
        onCommit?.(value);
      }
      return;
    }
    if (e.key === "Escape") {
      setOpen(false);
      setActive(-1);
    }
  };

  return (
    <div className="cpq-combo-wrap" ref={wrapRef}>
      <input
        ref={inputRef}
        type="text"
        className={`cpq-input cpq-combo ${inputClassName}`.trim()}
        style={inputStyle}
        value={shown.value}
        placeholder={shown.placeholder}
        disabled={disabled}
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
        aria-invalid={ariaInvalid}
        autoComplete="off"
        onChange={(e) => {
          // The text is passed straight through. Normalising here is what made a value
          // impossible to delete from the end.
          setFocusedEmpty(false);
          onChange(e.target.value);
          setOpen(true);
          setActive(-1);
        }}
        onFocus={() => {
          if (replaceOnType) setFocusedEmpty(true);
          setOpen(true);
        }}
        onBlur={(e) => {
          // Moving into the list is not leaving the field.
          const to = e.relatedTarget as Node | null;
          if (to && (wrapRef.current?.contains(to) || listRef.current?.contains(to))) return;
          setOpen(false);
          // Untouched: the box is empty but the answer is still the prediction.
          const untouched = ghosting;
          setFocusedEmpty(false);
          onCommit?.(untouched ? value : e.target.value);
        }}
        onKeyDown={onKeyDown}
      />
      <button
        type="button"
        className="cpq-combo-caret"
        tabIndex={-1}
        disabled={disabled}
        aria-label="Show options"
        // mousedown, not click: by click time the input has blurred and committed, which
        // would open the list and immediately close it again.
        onMouseDown={(e) => {
          e.preventDefault();
          setOpen((v) => !v);
          inputRef.current?.focus();
        }}
      />
      {open &&
        listed.length > 0 &&
        rect &&
        createPortal(
          <ul
            ref={listRef}
            className="cpq-combo-list"
            role="listbox"
            style={{ position: "fixed", left: rect.left, top: rect.top, width: rect.width }}
          >
            {listed.map((o, i) => (
              <li
                key={o.value}
                role="option"
                aria-selected={o.value === value}
                className={
                  "cpq-combo-option" +
                  (i === active ? " is-active" : "") +
                  (o.value === value ? " is-selected" : "")
                }
                onMouseEnter={() => setActive(i)}
                onMouseDown={(e) => {
                  e.preventDefault();
                  choose(o);
                }}
              >
                {o.label}
              </li>
            ))}
          </ul>,
          document.body
        )}
    </div>
  );
}
